import type { Partner } from '@/api/partners';
import { useCallback, useEffect, useState } from 'react';

/**
 * FE-A3 — "Legutóbbi partnerek" MRU (most-recently-used) gyors-választó.
 *
 * Phase-1: localStorage-only (FE, zero BE-dep). Eszköz-kötött — másik pulton
 * nem öröklődik. A cross-device hibrid (server-side last-activity + login-push)
 * Wave-2/Phase-2 upgrade-path (roadmap Q-2 árnyalat, ratifikált).
 *
 * Csak a megjelenítéshez szükséges mezőket tároljuk (id/name/type/city) — nem
 * a teljes Partner-t (kerüljük a stale PII-t localStorage-ban).
 */

export interface RecentPartner {
  id: string;
  name: string;
  type: Partner['type'];
  city?: string;
}

const STORAGE_KEY = 'kgc.recentPartners';
const MAX_RECENT = 5;

function readStore(): RecentPartner[] {
  try {
    const raw = window.localStorage?.getItem(STORAGE_KEY);
    if (!raw) return [];
    const parsed = JSON.parse(raw) as unknown;
    if (!Array.isArray(parsed)) return [];
    // Defensive: keep only well-formed entries.
    return parsed.filter(
      (p): p is RecentPartner =>
        !!p && typeof p === 'object' && typeof (p as RecentPartner).id === 'string'
    );
  } catch {
    return [];
  }
}

export function useRecentPartners(): {
  recent: RecentPartner[];
  push: (partner: Partner) => void;
} {
  const [recent, setRecent] = useState<RecentPartner[]>(() => readStore());

  // Re-read once on mount (SSR/hydration safety — initial state may be empty).
  useEffect(() => {
    setRecent(readStore());
  }, []);

  const push = useCallback((partner: Partner) => {
    const entry: RecentPartner = {
      id: partner.id,
      name: partner.name,
      type: partner.type,
      ...(partner.city !== undefined ? { city: partner.city } : {}),
    };
    setRecent(prev => {
      const next = [entry, ...prev.filter(p => p.id !== entry.id)].slice(0, MAX_RECENT);
      try {
        window.localStorage?.setItem(STORAGE_KEY, JSON.stringify(next));
      } catch {
        // ignore quota / unavailable storage
      }
      return next;
    });
  }, []);

  return { recent, push };
}
